/**
 * usePractitionerDashboard — Data hook for the Practitioner Dashboard screen.
 *
 * Fetches dashboard stats and recent client analyses via React Query.
 * Queries practitioner_consent (client list), client_analysis_outputs (analyses),
 * and practitioner_profiles (identity).
 *
 * Interface contract matches the spec in SPEC.md §4.3.
 *
 * Wire shape is owned by `PractitionerDashboardDto` in
 * `@dramadetector/shared/schemas`. Today the handler returns the minimal
 * profile payload; the optional `stats` and `recent_analyses` fields on
 * the DTO are reserved for future enrichment (NocoDB #2513).
 *
 * Data-proxy migration status: FULLY MIGRATED (2026-04-02)
 * ────────────────────────────────────────────────────────
 * All queries now go through data-proxy.
 *
 * Known limitations:
 * - Count queries use data-proxy .select() + client-side .length instead of
 *   the Supabase { count: 'exact', head: true } option (not supported by
 *   DataProxyClient). Less efficient but functionally equivalent.
 * - TODO (#2513): Client profiles lookup is cross-user. Data-proxy is
 *   owner-scoped so practitioner names degrade to 'Client' default.
 *   Needs a dedicated server-side RPC.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchJsonValidated,
  DtoValidationError,
} from '@dramadetector/api-client';
import {
  PractitionerDashboardDto,
  type PractitionerDashboard as PractitionerDashboardDtoShape,
} from '@dramadetector/shared/schemas';
import { getEdgeFunctionUrl } from '../lib/api-url';
import { practitionerProfileKeys } from './usePractitionerProfile';
import { getInitials } from './practitioner-utils';

export interface DashboardStats {
  activeClients: number;
  activeClientsDelta: number | null;
  analysesThisMonth: number;
  analysesThisMonthDelta: number | null;
  earningsThisMonth: string;
  clientsImprovingPct: number;
}

export interface RecentAnalysis {
  id: string;
  clientName: string;
  clientInitials: string;
  avatarIndex: number;
  patternKey: string;
  date: Date;
  dramaScore: number;
}

export interface UsePractitionerDashboardReturn {
  stats: DashboardStats | null;
  recentAnalyses: RecentAnalysis[];
  practitionerName: string;
  practitionerInitials: string;
  unreadNotificationCount: number;
  isLoading: boolean;
  isPractitioner: boolean;
  error: Error | null;
  refetch: () => void;
}

// Query key factory
export const practitionerDashboardKeys = {
  all: ['practitioner-dashboard'] as const,
  stats: (userId: string | null) =>
    [...practitionerDashboardKeys.all, 'stats', userId] as const,
  analyses: (userId: string | null) =>
    [...practitionerDashboardKeys.all, 'analyses', userId] as const,
};

export function usePractitionerDashboard(): UsePractitionerDashboardReturn {
  const { user, accessToken } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();

  // Fetch all dashboard data from a single endpoint
  const dashboardQuery = useQuery({
    queryKey: practitionerDashboardKeys.stats(userId),
    queryFn: async (): Promise<PractitionerDashboardDtoShape | null> => {
      if (!userId) return null;

      try {
        return await fetchJsonValidated(
          PractitionerDashboardDto,
          getEdgeFunctionUrl('get-practitioner-dashboard'),
        );
      } catch (err) {
        // The handler returns `null` for users without a practitioner_profiles
        // row — surfaces here as a DTO validation error. Treat as "not a
        // practitioner" rather than propagating the parse failure.
        if (err instanceof DtoValidationError) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 1000 * 60 * 2, // 2 minutes (dashboard is frequently viewed)
    gcTime: 1000 * 60 * 15,
    enabled: !!accessToken && !!userId,
    refetchOnMount: true,
  });

  const dashboardData = dashboardQuery.data;
  // Status check: today the handler returns just { id, practice_name,
  // practice_type } — no `status` field — so dashboardData being non-null
  // is a sufficient signal that the user has a profile row. The richer
  // `dashboardData.profile?.status === 'approved'` check kicks in once the
  // optional `profile` field starts being populated server-side.
  const isPractitioner = dashboardData?.profile
    ? dashboardData.profile.status === 'approved'
    : !!dashboardData;
  const practitionerName = dashboardData?.practice_name ?? '';
  const practitionerInitials = practitionerName
    ? getInitials(practitionerName)
    : '';

  // Derive stats from dashboard data
  const stats: DashboardStats | null = dashboardData?.stats ? {
    activeClients: dashboardData.stats.active_clients ?? 0,
    activeClientsDelta: dashboardData.stats.active_clients_delta ?? null,
    analysesThisMonth: dashboardData.stats.analyses_this_month ?? 0,
    analysesThisMonthDelta: dashboardData.stats.analyses_this_month_delta ?? null,
    earningsThisMonth: dashboardData.stats.earnings_this_month ?? '$0',
    clientsImprovingPct: dashboardData.stats.clients_improving_pct ?? 0,
  } : null;

  // Derive recent analyses from dashboard data
  const recentAnalyses: RecentAnalysis[] = (dashboardData?.recent_analyses ?? []).map(
    (row, idx) => {
      const name = row.client_name ?? 'Client';
      return {
        id: row.id,
        clientName: name,
        clientInitials: getInitials(name),
        avatarIndex: idx % 6,
        patternKey: row.category ?? 'unknown',
        date: new Date(row.created_at),
        dramaScore: row.score ?? 0,
      };
    }
  );

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: practitionerDashboardKeys.all,
    });
    queryClient.invalidateQueries({
      queryKey: practitionerProfileKeys.all,
    });
  }, [queryClient]);

  const isLoading = dashboardQuery.isLoading;
  const error = dashboardQuery.error as Error | null;

  return {
    stats,
    recentAnalyses,
    practitionerName,
    practitionerInitials,
    unreadNotificationCount: 0, // Notification system is future work
    isLoading: isLoading && !!accessToken,
    isPractitioner,
    error,
    refetch,
  };
}
